import { clarityEvent, clarityTagProps, clarityIdentify } from './clarity'

// Mixpanel project tokens are PUBLIC (they ship in the client JS bundle), so
// it's safe to keep both here and choose by the LIVE hostname rather than the
// build-time env file. This is the real fix for "dev events showing up in the
// prod Mixpanel project": the dev site (dev.jaaga.ai) is deployed with
// `npm run build`, which runs Vite in production mode and bakes in the PROD
// token from .env.production, so dev traffic was being routed to PROD.
// Selecting the token from window.location.hostname makes routing correct no
// matter which env file / build mode the deploy pipeline happened to use.
const MIXPANEL_TOKENS = {
  prod: '832fc2c096c4eadbdf40ed34e7e17496',
  dev: 'c1f025831f16d3cdcffc93134fd88669'
}

// Treat ONLY the real production domains as prod. Everything else,
// dev.jaaga.ai, staging, localhost, preview builds, is dev.
const PROD_HOSTS = ['www.jaaga.ai', 'jaaga.ai']
const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
const isProdHost = PROD_HOSTS.includes(hostname)
const ENVIRONMENT = isProdHost ? 'production' : 'development'
const MIXPANEL_TOKEN = isProdHost ? MIXPANEL_TOKENS.prod : MIXPANEL_TOKENS.dev

// react-snap runs the app in headless Chrome at BUILD time to prerender HTML;
// it sets navigator.userAgent to "ReactSnap". Skip analytics boot in that pass
// so the build doesn't fire phantom page views / init Mixpanel.
const isPrerender =
  typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap'

// ── Deferred SDK load ────────────────────────────────────────────────────────
// mixpanel-browser is ~118KB of the entry chunk and its first /track call sat on
// the page's critical path. The SDK is now dynamically imported, so it lands in
// its own chunk that downloads AFTER first paint, on idle or on the first event,
// whichever comes first. Every public helper below routes through `enqueue()`,
// which buffers calls in order and replays them once the SDK is live — so no
// event is lost and call sites are unchanged. The only behaviour change is that
// events reach Mixpanel a couple of seconds later than before.
let sdk = null // the real mixpanel object, once loaded
let loading = null // in-flight import promise, so we only ever load once
let failed = false // the import errored — stop queueing, every call is a no-op
const pending = [] // calls made before the SDK arrived, replayed in order

const loadSdk = () => {
  if (isPrerender || failed) return Promise.resolve(null)
  if (loading) return loading
  loading = import('mixpanel-browser')
    .then(({ default: mp }) => {
      mp.init(MIXPANEL_TOKEN, {
        debug: import.meta.env.DEV,
        track_pageview: true,
        persistence: 'localStorage'
      })
      // Stamp `environment` onto every event as a SUPER PROPERTY so you can
      // always filter / break down dev vs prod inside a project (a second safety
      // net on top of the per-host token routing above). Re-registered after
      // mixpanel.reset() in resetUser(), since reset() clears all super
      // properties.
      mp.register({ environment: ENVIRONMENT })
      sdk = mp
      // Replay everything that was queued while the chunk was downloading.
      while (pending.length) pending.shift()(mp)
      return mp
    })
    .catch(() => {
      // Blocked by an ad blocker / offline / chunk 404 after a deploy. Drop the
      // queue and make every future call a no-op rather than letting analytics
      // throw into the app — or letting `pending` grow without bound for the
      // rest of the session.
      failed = true
      pending.length = 0
      return null
    })
  return loading
}

/** Run `fn(mixpanel)` now if the SDK is up, otherwise queue it in order. */
const enqueue = (fn) => {
  if (isPrerender || failed) return
  if (sdk) {
    fn(sdk)
    return
  }
  pending.push(fn)
  loadSdk()
}

// Kick the download off once the browser is idle, so tracking on a page the user
// never interacts with still fires. Any earlier enqueue() call starts it sooner.
if (!isPrerender && typeof window !== 'undefined') {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => loadSdk(), { timeout: 5000 })
  } else {
    window.addEventListener('load', () => setTimeout(loadSdk, 2500))
  }
}

export const track = (event, props = {}) => {
  // DEV-ONLY debug line: prints every event to the browser console so you
  // can SEE in real time which events fire as you click (open DevTools →
  // Console). Stripped automatically in production builds (import.meta.env.DEV
  // is false there). Safe to delete once you've confirmed tracking works.
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(`📊 [Mixpanel] ${event}`, props)
  }
  // Stamp the timestamp NOW, not when the SDK finishes loading, so a queued
  // event still records the moment it actually happened.
  const timestamp = new Date().toISOString()
  enqueue((mp) => mp.track(event, { timestamp, ...props }))
  // Forward every funnel event to Microsoft Clarity so the same step
  // sequence powers Clarity's "Funnels" report, and, more importantly,
  // session recordings of users who dropped off at each step. The
  // helpers no-op when Clarity isn't loaded (blocked domain/route or
  // pre-boot first render, see providers/ClarityProvider.jsx), so this
  // is safe to call unconditionally.
  //
  // Properties become session TAGS (filtered through a whitelist in
  // lib/clarity.js to keep the tag space readable). High-cardinality
  // ids and timestamps are intentionally NOT forwarded, Mixpanel owns
  // analytical drilldown, Clarity owns "show me the session".
  clarityEvent(event)
  clarityTagProps(props)
}

/**
 * Normalize a raw phone string into a STABLE distinct_id key.
 * Strips everything but digits and keeps the last 10, so the OTP path
 * ("+91" + number) and the HomePage path (`customerInfo.mobile`, which may
 * omit the country code) collapse to the SAME id for the same person.
 * Without this, one user splits into two profiles and the funnel drop-off
 * numbers become wrong. Returns null when there aren't enough digits to be
 * a real number, so identify() can fall back to the internal userId.
 * (Assumes 10-digit national mobile numbers, correct for India; tell me
 * if you need to keep the country code for international users.)
 */
const mobileDistinctId = (raw) => {
  if (!raw) return null
  const digits = String(raw).replace(/\D/g, '')
  return digits.length >= 10 ? digits.slice(-10) : null
}

export const identify = (userId, userProps = {}) => {
  // Prefer the MOBILE NUMBER as the distinct_id (normalized so a single
  // person is never split across login paths); fall back to the internal
  // userId when no usable phone is present. This is what makes the Events
  // view show the phone number in the distinct_id column.
  const distinctId = mobileDistinctId(userProps.$phone || userProps.mobile) || userId
  if (!distinctId) return
  enqueue((mp) => {
    mp.identify(distinctId)
    mp.people.set(userProps)
  })
  // Attach the mobile number to EVERY future event, not just the profile.
  // `people.set` above writes $phone onto the user PROFILE (Users panel
  // only). To make the number visible on each event in the Events view,
  // and filterable/breakable in funnel reports, we register it as a
  // SUPER PROPERTY: Mixpanel then stamps `mobile` onto every event the
  // user fires from now on (persisted in localStorage, so it survives
  // reloads). Only the phone is promoted here (not lastLoginAt/loginMode
  // etc.) to keep event payloads small and the property list readable.
  // Note: super properties persist until mixpanel.reset(), call that on
  // logout so a previous user's number doesn't ride along on the next,
  // anonymous, session.
  const mobile = userProps.$phone || userProps.mobile
  if (mobile) {
    enqueue((mp) => mp.register({ mobile }))
  }
  // Mirror the identity into Clarity so multi-session recordings for a
  // single customer merge under one identifier in the dashboard, and so
  // support can search by name. Falls back gracefully on Mixpanel
  // reserved props ($name / $email).
  clarityIdentify(distinctId, userProps?.$name || userProps?.name)
}

/**
 * Remove one or more profile properties from the current Mixpanel user.
 * Use this to clear stale values that were written before a code fix,
 * `people.set()` only overwrites, it never deletes. Without an unset()
 * call, a previously-written `$email: "undefined"` stays on the profile
 * forever (showing as the literal text "undefined" in the Users panel).
 *
 * Accepts a single property name or an array of names:
 *   peopleUnset('$email')
 *   peopleUnset(['$email', 'mobile_old'])
 */
export const peopleUnset = (propNames) => {
  const list = Array.isArray(propNames) ? propNames : [propNames]
  if (!list.length) return
  enqueue((mp) => mp.people.unset(list))
}

/**
 * Reset Mixpanel on logout. Generates a fresh anonymous distinct_id and,
 * crucially, clears all SUPER PROPERTIES (including the `mobile` we
 * register in identify()). Without this, the logged-out user's phone
 * number would keep riding along on the next, anonymous, session's events
 * and could even get merged onto the next person who logs in on this
 * browser. Call from the app's logout flow (see common/utils/logout.js).
 */
export const resetUser = () => {
  enqueue((mp) => {
    mp.reset()
    // reset() clears ALL super properties, including the `environment` we set at
    // boot. Re-register it so events fired by the next anonymous session are
    // still tagged with the correct environment.
    mp.register({ environment: ENVIRONMENT })
  })
}

// The default export is a queue-backed stand-in for the raw SDK, kept so that
// `import mixpanel from '.../lib/mixpanel'` keeps working now that the real
// object only exists after an async import. Use the named helpers above by
// preference — they also mirror events into Clarity.
export default {
  track: (event, props) => enqueue((mp) => mp.track(event, props)),
  identify: (id) => enqueue((mp) => mp.identify(id)),
  register: (props) => enqueue((mp) => mp.register(props)),
  reset: () => enqueue((mp) => mp.reset()),
  people: {
    set: (props) => enqueue((mp) => mp.people.set(props)),
    unset: (props) => enqueue((mp) => mp.people.unset(props))
  }
}